import { useState } from 'preact/hooks'
import { findCoveringDomain, normalizeApprovedDomain, APPROVED_DOMAINS_MAX_COUNT } from '@/utils/approvedDomain'
import { parseValidationErrors } from '@/utils/errorHandling'
import type { CompanyTrackingViewDto, DerivedApprovedDomain } from '@/types/tracking'
import type { ApiResponse } from '@/api/client'

type SaveApprovedDomains = (
  domains: string[],
) => Promise<ApiResponse<CompanyTrackingViewDto> | null>

/** State + handlers the ApprovedDomainsPanel renders. Keeps the component presentation-only. */
export interface ApprovedDomainsEditor {
  /** Read-only, system-derived rows (company profile + External-mode landing pages). */
  derived: DerivedApprovedDomain[]
  /** Editable custom allowlist entries. */
  custom: string[]
  adding: boolean
  newDomain: string
  error: string | null
  saving: boolean
  /** The custom entry currently being removed, so only its row shows a spinner. */
  removing: string | null
  /** True once the custom list has hit APPROVED_DOMAINS_MAX_COUNT. */
  atLimit: boolean
  openAddForm: () => void
  closeAddForm: () => void
  setNewDomain: (value: string) => void
  /** Clears any inline error (used while the user edits the input). */
  clearError: () => void
  handleAdd: () => Promise<void>
  handleRemove: (domain: string) => Promise<void>
}

/**
 * Encapsulates the approved-domains editor's local state, client-side validation (mirroring the
 * server normalizer), and the PATCH-per-mutation save orchestration for ORB-458. Each add/remove
 * persists immediately via `onSave`; 422s are surfaced inline while other failures are toasted by
 * the hook that owns `onSave`.
 */
export function useApprovedDomainsEditor(
  tracking: CompanyTrackingViewDto,
  onSave: SaveApprovedDomains,
): ApprovedDomainsEditor {
  const custom = tracking.ApprovedDomains
  const derived = tracking.DerivedDomains

  const [adding, setAdding] = useState(false)
  const [newDomain, setNewDomain] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [removing, setRemoving] = useState<string | null>(null)

  const atLimit = custom.length >= APPROVED_DOMAINS_MAX_COUNT

  const openAddForm = () => {
    setNewDomain('')
    setError(null)
    setAdding(true)
  }

  const closeAddForm = () => {
    setAdding(false)
    setNewDomain('')
    setError(null)
  }

  const clearError = () => {
    if (error) setError(null)
  }

  // Surface an API 422 (invalid / too many domains) inline; other failures are toasted by the hook.
  const applyServerError = (
    res: ApiResponse<CompanyTrackingViewDto> | null,
    fallback: string,
  ) => {
    if (res && res.status === 422) {
      const messages = parseValidationErrors(res)
      setError(messages[0]?.Message ?? fallback)
    }
  }

  const handleAdd = async () => {
    const host = normalizeApprovedDomain(newDomain)
    if (!host) {
      setError('Enter a valid domain, e.g. example.com — no wildcards, paths, or spaces.')
      return
    }
    // Coverage, not equality: an existing entry already approves its subdomains at the API, so adding
    // one buys nothing. Same rule the custom-LP nudge uses, so the two surfaces never disagree.
    const coveredBy = findCoveringDomain(host, [...derived.map((d) => d.Host), ...custom])
    if (coveredBy) {
      setError(
        coveredBy === host
          ? 'That domain is already approved.'
          : `Already covered by ${coveredBy} — that entry approves its subdomains.`,
      )
      return
    }
    if (atLimit) {
      setError(`You can add at most ${APPROVED_DOMAINS_MAX_COUNT} approved domains.`)
      return
    }

    setSaving(true)
    setError(null)
    const res = await onSave([...custom, host])
    setSaving(false)

    if (!res?.data) {
      applyServerError(res, 'Could not add that domain.')
      return
    }
    // Success: the hook adopted the new DTO. Keep the box open with a cleared input for quick entry.
    setNewDomain('')
  }

  const handleRemove = async (domain: string) => {
    setRemoving(domain)
    const res = await onSave(custom.filter((d) => d !== domain))
    setRemoving(null)
    // A remove that 422s is unexpected (the list was already valid) — the hook toasts non-422 failures.
    if (res && res.status === 422) applyServerError(res, 'Could not remove that domain.')
  }

  return {
    derived,
    custom,
    adding,
    newDomain,
    error,
    saving,
    removing,
    atLimit,
    openAddForm,
    closeAddForm,
    setNewDomain,
    clearError,
    handleAdd,
    handleRemove,
  }
}
